import React, { useState, useEffect } from 'react';
import { Search, Edit2, CheckCircle, XCircle, Eye, UserPlus } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface UserAccess {
  id: string;
  user_id: number;
  telegram_username: string;
  telegram_first_name: string;
  telegram_last_name: string;
  access_level: 'free' | 'premium' | 'enterprise';
  is_active: boolean;
  max_receipts_per_month: number;
  receipts_used_this_month: number;
  created_at: string;
}

export default function UserManagement() {
  const [users, setUsers] = useState<UserAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserAccess | null>(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_access')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.telegram_username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.telegram_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.user_id.toString().includes(searchTerm)
  );

  const updateUser = async (userId: string, updates: Partial<UserAccess>) => {
    try {
      const { error } = await supabase
        .from('user_access')
        .update(updates)
        .eq('id', userId);

      if (error) throw error;
      await loadUsers();
      setEditMode(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const getAccessBadgeColor = (level: string) => {
    switch (level) {
      case 'enterprise':
        return 'bg-purple-100 text-purple-800';
      case 'premium':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h2>
        <div className="text-sm text-gray-700 dark:text-gray-300">{users.length} total users</div>
      </div>

      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-800 dark:text-gray-200 dark:text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by Telegram ID, username, or name..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Telegram ID</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">User</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Access Level</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Usage</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-800 dark:text-gray-200">
                  Loading users...
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-800 dark:text-gray-200">
                  No users found
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-mono text-sm">{user.user_id}</td>
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-gray-900">
                        {user.telegram_first_name} {user.telegram_last_name}
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200">@{user.telegram_username || 'N/A'}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`badge ${getAccessBadgeColor(user.access_level)}`}>
                      {user.access_level.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-brand-primary h-2 rounded-full"
                          style={{
                            width: `${(user.receipts_used_this_month / user.max_receipts_per_month) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-gray-600">
                        {user.receipts_used_this_month}/{user.max_receipts_per_month}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {user.is_active ? (
                      <span className="badge-success flex items-center gap-1 w-fit">
                        <CheckCircle className="w-3 h-3" />
                        Active
                      </span>
                    ) : (
                      <span className="badge-error flex items-center gap-1 w-fit">
                        <XCircle className="w-3 h-3" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setEditMode(true);
                        }}
                        className="text-brand-primary hover:text-brand-secondary"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editMode && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Edit User Access</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Access Level</label>
                <select
                  value={selectedUser.access_level}
                  onChange={(e) =>
                    setSelectedUser({ ...selectedUser, access_level: e.target.value as any })
                  }
                  className="input-field"
                >
                  <option value="free">Free</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Receipts/Month
                </label>
                <input
                  type="number"
                  value={selectedUser.max_receipts_per_month}
                  onChange={(e) =>
                    setSelectedUser({
                      ...selectedUser,
                      max_receipts_per_month: parseInt(e.target.value),
                    })
                  }
                  className="input-field"
                />
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedUser.is_active}
                    onChange={(e) => setSelectedUser({ ...selectedUser, is_active: e.target.checked })}
                    className="w-4 h-4 text-brand-primary"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => updateUser(selectedUser.id, {
                    access_level: selectedUser.access_level,
                    max_receipts_per_month: selectedUser.max_receipts_per_month,
                    is_active: selectedUser.is_active,
                  })}
                  className="btn-primary flex-1"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setSelectedUser(null);
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
